import React, { useState, useEffect, useCallback } from 'react';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { RefreshCw, Search, ChevronRight, Copy, Bot, Clock, Cpu, Coins, Loader2 } from 'lucide-react';

const FEATURE_LABEL = {
  nl_query: 'AI Explore', insights: 'Insights', data_prep: 'Data Prep', dashboard_build: 'Dashboard Builder',
  modeling: 'Modeling', import_parse: 'Import Parse', classify: 'Classify', anomaly: 'Anomaly', rca: 'RCA',
  general: 'General',
};
const featLabel = (f) => FEATURE_LABEL[f] || (f || '').replace(/[._]/g, ' ');

function Block({ label, text }) {
  return (
    <div className="mt-2">
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] uppercase tracking-wide text-muted-foreground/70 font-semibold">{label}</span>
        <button
          onClick={() => { navigator.clipboard.writeText(text || ''); toast.success(`${label} copied`); }}
          className="text-[10px] text-muted-foreground hover:text-primary flex items-center gap-1"
          data-testid={`copy-${label.toLowerCase().replace(/\s+/g, '-')}`}>
          <Copy className="w-3 h-3" /> Copy
        </button>
      </div>
      <pre className="text-[11px] leading-relaxed font-mono whitespace-pre-wrap break-words bg-[#0d1117] text-slate-200 border border-border/40 rounded-lg p-3 max-h-80 overflow-auto">
        {text || <span className="text-muted-foreground/70">(empty)</span>}
      </pre>
    </div>
  );
}

export default function AiTraceLogPage() {
  const { user } = useAuth();
  const [data, setData] = useState({ traces: [], features: [], tenants: [] });
  const [loading, setLoading] = useState(true);
  const [feature, setFeature] = useState('nl_query');
  const [tenant, setTenant] = useState('*');
  const [q, setQ] = useState('');
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { feature, limit: 200 };
      if (tenant) params.tenant = tenant;
      if (q.trim()) params.q = q.trim();
      const res = await api.getAiTraces(params);
      setData(res);
    } catch (e) { toast.error(e.message || 'Failed to load AI traces'); }
    setLoading(false);
  }, [feature, tenant, q]);

  useEffect(() => { load(); }, [feature, tenant]); // eslint-disable-line react-hooks/exhaustive-deps

  if (user && user.role !== 'super_admin') {
    return <div className="p-8 text-sm text-muted-foreground" data-testid="ai-trace-denied">AI Trace Log is available to super admins only.</div>;
  }

  return (
    <div className="p-6 space-y-5" data-testid="ai-trace-log-page">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-heading font-bold text-foreground flex items-center gap-2"><Bot className="w-6 h-6 text-primary" /> AI Trace Log</h1>
          <p className="text-sm text-muted-foreground mt-1">Exact prompt sent to and response received from the AI agent for every call — for troubleshooting.</p>
        </div>
        <Button variant="outline" size="sm" onClick={load} className="rounded-lg text-xs gap-1.5" data-testid="ai-trace-refresh">
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Refresh
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Select value={feature} onValueChange={setFeature}>
          <SelectTrigger className="w-52 text-xs h-9" data-testid="ai-trace-feature"><SelectValue placeholder="Feature" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All features</SelectItem>
            {(data.features || []).map(f => <SelectItem key={f} value={f}>{featLabel(f)}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={tenant || 'current'} onValueChange={(v) => setTenant(v === 'current' ? '' : v)}>
          <SelectTrigger className="w-52 text-xs h-9" data-testid="ai-trace-tenant"><SelectValue placeholder="Tenant" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="current">My tenant</SelectItem>
            <SelectItem value="*">All tenants</SelectItem>
            {(data.tenants || []).map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') load(); }}
            placeholder="Search prompt / response / model…" className="pl-8 text-xs h-9" data-testid="ai-trace-search" />
        </div>
      </div>

      <div className="text-[11px] text-muted-foreground">{data.traces?.length || 0} trace{(data.traces?.length || 0) !== 1 ? 's' : ''}</div>

      <div className="space-y-2" data-testid="ai-trace-list">
        {loading && (data.traces || []).length === 0 && <div className="text-sm text-muted-foreground py-10 text-center">Loading traces…</div>}
        {!loading && (data.traces || []).length === 0 && (
          <div className="glass-card rounded-2xl p-10 text-center text-sm text-muted-foreground" data-testid="ai-trace-empty">
            No AI traces yet for this filter. Run an AI Explore query, then refresh.
          </div>
        )}
        {(data.traces || []).map(t => {
          const open = expanded === t.trace_id;
          return (
            <div key={t.trace_id} className="glass-card rounded-xl overflow-hidden" data-testid={`ai-trace-${t.trace_id}`}>
              <button onClick={() => setExpanded(open ? null : t.trace_id)}
                className="w-full flex items-center gap-3 p-3 text-left hover:bg-muted/10 transition-colors" data-testid={`ai-trace-toggle-${t.trace_id}`}>
                <ChevronRight className={`w-4 h-4 text-muted-foreground flex-shrink-0 transition-transform ${open ? 'rotate-90' : ''}`} />
                <Badge className="text-[9px] bg-primary/15 text-primary border-primary/25 flex-shrink-0">{featLabel(t.feature)}</Badge>
                <span className="text-xs text-foreground/80 font-mono truncate flex-1">{(t.prompt || '').slice(0, 120) || '(no prompt)'}</span>
                <span className="hidden md:flex items-center gap-1 text-[10px] text-muted-foreground flex-shrink-0"><Cpu className="w-3 h-3" />{t.model}</span>
                <span className="hidden lg:flex items-center gap-1 text-[10px] text-muted-foreground flex-shrink-0"><Clock className="w-3 h-3" />{t.latency_ms}ms</span>
                <span className="hidden lg:flex items-center gap-1 text-[10px] text-muted-foreground flex-shrink-0">{(t.prompt_tokens || 0)}→{(t.completion_tokens || 0)} tok</span>
                <span className="text-[10px] text-muted-foreground/80 flex-shrink-0">{t.created_at ? new Date(t.created_at).toLocaleString() : ''}</span>
              </button>
              {open && (
                <div className="px-4 pb-4 border-t border-border/30">
                  <div className="flex flex-wrap gap-3 mt-3 text-[10px] text-muted-foreground">
                    <span className="flex items-center gap-1"><Cpu className="w-3 h-3" /> {t.provider} · {t.model}</span>
                    <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {t.latency_ms} ms</span>
                    <span>tokens: {t.prompt_tokens ?? '—'} in / {t.completion_tokens ?? '—'} out</span>
                    {t.cost_usd != null && <span className="flex items-center gap-1"><Coins className="w-3 h-3" /> ${Number(t.cost_usd).toFixed(5)}</span>}
                    <span>tenant: {t.tenant_id || '—'}</span>
                    {t.workspace_id && <span>ws: {t.workspace_id}</span>}
                  </div>
                  <Block label="System prompt" text={t.system} />
                  <Block label="User prompt" text={t.prompt} />
                  <Block label="AI response" text={t.response} />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
